import React, { useEffect } from 'react';
import { Spin, Tooltip } from 'antd';
import { Sparkles, Lock } from 'lucide-react';
import { useOutline } from '../hooks/useOutline';
import { useActiveHeading } from '../hooks/useActiveHeading';
import { useEditorContext } from '../contexts/EditorContext';
import { scrollEditorToPos } from '../utils/editorScroll';
import { PANEL_TAB } from './constants';
import styles from '../styles/index.module.scss';

/**
 * 文档大纲：章节 / 小节列表，高亮当前阅读位置，点击跳转；
 * 点击可改稿的小节会同时把它设为改稿对象。
 */
export const EditorOutline = ({ onNavigate }) => {
    const { editor, uiState, scrollContainerRef, selectionState } = useEditorContext();
    const outline = useOutline(editor);
    const [activeId, setActiveId] = useActiveHeading(editor, outline, scrollContainerRef);

    // 在正文里点了标题（或顶栏改稿默认选中章节）时，大纲同步高亮
    useEffect(() => {
        const info = selectionState.selectedHeadingInfo;
        if (selectionState.isHeadingSelected && info) {
            setActiveId(info.blockId || `heading-${info.pos}`);
        }
    }, [selectionState.isHeadingSelected, selectionState.selectedHeadingInfo, setActiveId]);

    const wordCount = editor?.storage?.characterCount?.characters?.() || 0;

    const handleClick = (item) => {
        if (!editor) return;
        setActiveId(item.id);
        // 选中标题节点：工具面板会把它识别为改稿对象
        if (!item.isProtected) {
            editor.commands.setNodeSelection(item.pos);
            uiState.activeTab = PANEL_TAB.CHAPTER;
        }
        window.requestAnimationFrame(() => {
            scrollEditorToPos(editor, item.pos, {
                container: scrollContainerRef.current,
                offset: 32,
            });
        });
        if (onNavigate) onNavigate(item);
    };

    return (
        <div className={styles.outline}>
            <div className={styles.outlineHeader}>
                <span className={styles.outlineTitle}>文档大纲</span>
                {outline.length > 0 && (
                    <span className={styles.outlineCount}>{outline.length} 节</span>
                )}
            </div>
            <div className={styles.outlineBody}>
                {uiState.loading ? (
                    <div className={styles.outlineLoading}>
                        <Spin size="small" />
                        <span>加载中…</span>
                    </div>
                ) : outline.length === 0 ? (
                    <div className={styles.outlineEmpty}>暂无大纲</div>
                ) : (
                    <ul className={styles.outlineList}>
                        {outline.map((item) => {
                            const active = item.id === activeId;
                            const className = [
                                styles.outlineItem,
                                styles[`outlineLevel${Math.min(item.level, 3)}`],
                                active ? styles.outlineItemActive : '',
                                item.isProtected ? styles.outlineItemProtected : '',
                            ]
                                .filter(Boolean)
                                .join(' ');
                            return (
                                <li key={item.id}>
                                    <button
                                        type="button"
                                        className={className}
                                        onClick={() => handleClick(item)}
                                        title={item.text}
                                    >
                                        <span className={styles.outlineItemText}>{item.text}</span>
                                        {item.rewritable && (
                                            <Tooltip title="可改稿" placement="right">
                                                <span className={styles.outlineItemMark}>
                                                    <Sparkles size={12} />
                                                </span>
                                            </Tooltip>
                                        )}
                                        {item.isProtected && (
                                            <Tooltip title="参考文献由系统维护，不可编辑" placement="right">
                                                <span className={styles.outlineItemMark}>
                                                    <Lock size={12} />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            <div className={styles.outlineFooter}>
                <span>全文字数</span>
                <strong>{wordCount.toLocaleString()}</strong>
            </div>
        </div>
    );
};

export default EditorOutline;
